import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { defaultInterest } from '../../domain/lifecycleCalc';
import { date, daysBetween, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useInvestorScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { AboutThisPage } from '../../ui/primitives';
import { Card, InvestorTitle, SectionLabel, shortEntityName } from '../common';

function CopyValue({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        <Tooltip title="Copy">
          <IconButton
            size="small"
            aria-label={`Copy ${label}`}
            onClick={() => {
              void navigator.clipboard?.writeText(value).catch(() => undefined);
              toast(`${label} copied`, 'info');
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export default function InvestorPaymentsPage() {
  const scope = useInvestorScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const callLines = scope.calls.flatMap((call) =>
    call.lines.filter((line) => scope.holderIds.has(line.holderId)).map((line) => ({ call, line })),
  );
  const due = callLines.filter((item) => item.line.status !== 'PAID');
  const paid = callLines.filter((item) => item.line.status === 'PAID');
  const advices = scope.distributions
    .flatMap((distribution) =>
      distribution.lines
        .filter((line) => scope.holderIds.has(line.holderId))
        .map((line) => ({ distribution, line })),
    )
    .sort((left, right) =>
      right.distribution.paymentDate.localeCompare(left.distribution.paymentDate),
    );
  const pendingBank = (holderId: string, callId: string) =>
    state.bankLines.some(
      (line) =>
        line.suggestedHolderId === holderId &&
        line.suggestedCallId === callId &&
        line.status === 'SUGGESTED',
    );

  return (
    <>
      <InvestorTitle
        title="Payments"
        subtitle="Capital calls to pay, and distributions paid to you."
      />
      <AboutThisPage>
        <p>
          Each call has its own BPAY reference so your payment is matched automatically.
          Distribution advices show the gross amount, any tax withheld and what reached your bank
          (or was reinvested).
        </p>
      </AboutThisPage>
      {scope.paymentExceptions.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button component={RouterLink} to="/investor/tasks" size="small">
              Fix now
            </Button>
          }
        >
          We couldn&apos;t pay{' '}
          {scope.paymentExceptions.length === 1
            ? 'one payment'
            : `${scope.paymentExceptions.length} payments`}{' '}
          (
          {money(
            scope.paymentExceptions.reduce((sum, item) => sum + item.amount, 0),
            true,
          )}
          ) to your bank account. Confirm your details and we&apos;ll pay straight away.
        </Alert>
      )}
      <SectionLabel>To pay</SectionLabel>
      {due.length === 0 && (
        <Card>
          <Typography variant="body2" color="text.secondary">
            No capital calls outstanding.
          </Typography>
        </Card>
      )}
      <Stack spacing={1.5}>
        {due.map(({ call, line }) => {
          const fund = scope.funds.find((item) => item.id === call.fundId)!;
          const holder = scope.holders.find((item) => item.id === line.holderId);
          const days = daysBetween(DEMO_TODAY, call.dueDate);
          const waiting = pendingBank(line.holderId, call.id);
          return (
            <Card key={`${call.id}-${line.holderId}`} sx={{ borderLeft: '4px solid #b26a00' }}>
              <Box
                sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {holder ? shortEntityName(holder) : ''}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                    {fund.name} · capital call {call.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {call.purpose}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', mt: 1.5 }}>
                    {money(line.amount - line.paid)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: days < 0 ? 'error.main' : 'warning.main', fontWeight: 600 }}
                  >
                    {days < 0
                      ? `Overdue by ${-days} days`
                      : `Due ${date(call.dueDate)} (${days} days)`}
                  </Typography>
                  {line.paid > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {money(line.paid)} already received
                    </Typography>
                  )}
                  {days < 0 && defaultInterest(line, call.dueDate, DEMO_TODAY) > 0 && (
                    <Typography variant="caption" color="error.main" component="div">
                      Default interest so far:{' '}
                      {money(defaultInterest(line, call.dueDate, DEMO_TODAY), true)}
                      {line.noticeDate ? ` · default notice issued ${date(line.noticeDate)}` : ''}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ bgcolor: 'background.default', borderRadius: '6px', p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Pay by BPAY
                  </Typography>
                  <Stack spacing={0.75}>
                    <CopyValue label="Biller code" value={fund.bpayBillerCode} />
                    <CopyValue label="Reference" value={line.crn} />
                  </Stack>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Or bank transfer
                  </Typography>
                  <Stack spacing={0.75}>
                    <CopyValue label="BSB" value={fund.bank.bsb} />
                    <CopyValue label="Account" value={fund.bank.accountNumber} />
                    <CopyValue label="Reference" value={line.crn} />
                  </Stack>
                </Box>
              </Box>
              {waiting ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Payment received by the bank and being matched. We&apos;ll confirm when it&apos;s
                  applied.
                </Alert>
              ) : (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      dispatch({
                        type: 'INVESTOR_PAY_CALL',
                        holderId: line.holderId,
                        callId: call.id,
                      });
                      toast(
                        'Simulated: BPAY payment sent. It now appears in the manager’s bank feed as a suggested match.',
                      );
                    }}
                  >
                    Simulate BPAY payment
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setViewing(
                        scope.documents.find(
                          (doc) =>
                            doc.kind === 'CALL_NOTICE' &&
                            doc.refId === call.id &&
                            doc.holderId === line.holderId,
                        ) ?? null,
                      )
                    }
                  >
                    View notice
                  </Button>
                </Stack>
              )}
            </Card>
          );
        })}
      </Stack>
      {scope.capitalReturns.length > 0 && (
        <>
          <SectionLabel>Capital returned</SectionLabel>
          <Card sx={{ p: 0 }}>
            {scope.capitalReturns
              .flatMap((capitalReturn) =>
                capitalReturn.lines
                  .filter((line) => scope.holderIds.has(line.holderId))
                  .map((line) => ({ capitalReturn, line })),
              )
              .map(({ capitalReturn, line }, index) => (
                <Stack
                  key={`${capitalReturn.id}-${line.holderId}`}
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: index ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {scope.funds.find((fund) => fund.id === capitalReturn.fundId)?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {capitalReturn.kind === 'FINAL'
                        ? 'Repaid in full and wound up'
                        : `${capitalReturn.returnPct}% returned`}{' '}
                      · {date(capitalReturn.paymentDate)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                    +{money(line.amount + line.finalIncome, true)}
                  </Typography>
                </Stack>
              ))}
          </Card>
        </>
      )}
      <SectionLabel>Distributions received</SectionLabel>
      <Card sx={{ p: 0 }}>
        {advices.slice(0, 12).map(({ distribution, line }, index) => {
          const fund = scope.funds.find((item) => item.id === distribution.fundId);
          const holder = scope.holders.find((item) => item.id === line.holderId);
          const withheld = line.tfnWithholding + line.nrWithholding;
          return (
            <Stack
              key={`${distribution.id}-${line.holderId}`}
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={1}
              sx={{
                px: 2,
                py: 1.5,
                borderTop: index ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fund?.code} · period ending {date(distribution.periodEnd)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {holder ? shortEntityName(holder) : ''} · paid {date(distribution.paymentDate)} ·{' '}
                  {line.units.toLocaleString('en-AU')} units @ {distribution.centsPerUnit} cpu
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {withheld > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${money(withheld, true)} withheld`}
                  />
                )}
                {line.method === 'DRP' && (
                  <Chip size="small" variant="outlined" label="Reinvested" />
                )}
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                  +{money(line.net, true)}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Card>
      {paid.length > 0 && (
        <>
          <SectionLabel>Calls paid</SectionLabel>
          <Card sx={{ p: 0 }}>
            {paid.map(({ call, line }, index) => (
              <Stack
                key={`${call.id}-${line.holderId}`}
                direction="row"
                justifyContent="space-between"
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop: index ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">
                  {scope.funds.find((item) => item.id === call.fundId)?.code} call {call.number}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {money(line.paid)}
                </Typography>
              </Stack>
            ))}
          </Card>
        </>
      )}
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
